import { useEffect, useState, useRef } from 'react';
import analyticsService from '../services/analyticsService';
import { updateMessageModerationStatus } from '../services/backendService';
import { useLanguage, useTranslate } from '../lib/i18n';
import { useInfoCardStacks } from '../hooks/queries/useInfoCards';
import { useConfig } from '../hooks';
import PrimarySection from './PrimarySection';
import Button from './common/Button';
import HelpCards from './HelpCards';
import InformationCards from './InfoCardStackList';
import MarkdownRenderer from './MarkdownRenderer';

const pages = {
  INITIAL: 'initial',
  CONTINUE: 'continue',
  WRONG: 'wrong',
  THOUGHTS: 'thoughts',
  INFORMATION: 'information',
  IMMEDIATE: 'immediate',
  EMERGENCY_ONLY: 'emergency_only',
};

interface Props {
  onClearSuicideRisk: () => void;
  messageId?: string | null;
}

const SuicideQuestions = ({ onClearSuicideRisk, messageId }: Props) => {
  const [currentPage, setCurrentPage] = useState(pages.INITIAL);
  const componentRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslate();
  const { language } = useLanguage();

  const { data: config } = useConfig();
  const { data } = useInfoCardStacks(language, config);
  const suicideStacks = data?.getStacksForCategory('suicidal') ?? [];

  useEffect(() => {
    analyticsService.trackEvent({
      eventName: 'suicidalThoughtsPageViewed',
      properties: { page: currentPage },
    });
  }, [currentPage]);

  useEffect(() => {
    analyticsService.trackEvent({
      eventName: 'suicidalThoughtsTriggered',
    });
  }, []);

  useEffect(() => {
    if (componentRef.current) {
      // Try scrollIntoView with smooth behavior first
      try {
        componentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        // Fallback to simple scrollIntoView if smooth scroll is not supported
        componentRef.current.scrollIntoView();
      }
    }
  }, [currentPage]);

  const handleModeration = async (moderationStatus: 'ACCEPTED' | 'REJECTED') => {
    if (!messageId) {
      console.error('Message ID is undefined');
      return;
    }

    try {
      await updateMessageModerationStatus({
        messageId,
        moderationStatus,
      });
    } catch (error) {
      console.error('Error updating moderation status:', error);
    }
  };

  const thoughtsOptions = [
    {
      id: 'option1',
      text: t('suicideQuestions.thoughtsOptions.option1'),
      page: pages.INFORMATION,
      severityEvent: 'suicidalThoughtsLowRiskSelected' as const,
    },
    {
      id: 'option2',
      text: t('suicideQuestions.thoughtsOptions.option2'),
      page: pages.IMMEDIATE,
      severityEvent: 'suicidalThoughtsMediumRiskSelected' as const,
    },
    {
      id: 'option3',
      text: t('suicideQuestions.thoughtsOptions.option3'),
      page: pages.EMERGENCY_ONLY,
      severityEvent: 'suicidalThoughtsHighRiskSelected' as const,
    },
  ];

  const onConfirmSuicidalThoughts = () => {
    handleModeration('ACCEPTED');
    setCurrentPage(pages.THOUGHTS);
    analyticsService.trackEvent({
      eventName: 'suicidalThoughtsConfirmed',
    });
  };

  const onDenySucidalThoughts = () => {
    handleModeration('REJECTED');
    setCurrentPage(pages.WRONG);
    onClearSuicideRisk();
    analyticsService.trackEvent({
      eventName: 'suicidalThoughtsDenied',
    });
  };

  const renderPage = () => {
    switch (currentPage) {
      case pages.INITIAL:
        return (
          <PrimarySection title={t('suicideQuestions.title')}>
            <div className="mb-4 space-y-4 text-center">
              <div className="text-sm">
                <MarkdownRenderer markdown={t('suicideQuestions.initial.description')} />
              </div>
              <p className="text-sm">{t('suicideQuestions.initial.question')}</p>
            </div>
            <div className="space-y-2">
              <Button variant="primary" onClick={onConfirmSuicidalThoughts}>
                {t('suicideQuestions.initial.option1')}
              </Button>
              <Button variant="primary" onClick={onDenySucidalThoughts}>
                {t('suicideQuestions.initial.option2')}
              </Button>
            </div>
          </PrimarySection>
        );

      case pages.WRONG:
        return (
          <PrimarySection title={t('suicideQuestions.title')}>
            <div className="space-y-4 text-center">
              <p className="text-sm">{t('suicideQuestions.wrong.description')}</p>
              <p className="text-sm">{t('suicideQuestions.wrong.question')}</p>
            </div>
          </PrimarySection>
        );

      case pages.CONTINUE:
        return (
          <PrimarySection title={t('suicideQuestions.title')}>
            <div className="space-y-4 text-center">
              <p className="text-sm">{t('suicideQuestions.continue.description')}</p>
              <p className="text-sm">{t('suicideQuestions.continue.question')}</p>
            </div>
          </PrimarySection>
        );

      case pages.THOUGHTS:
        return (
          <PrimarySection title={t('suicideQuestions.title')}>
            <div className="mb-4 space-y-4 text-center">
              <p className="text-sm">{t('suicideQuestions.thoughts.description1')}</p>
              <p className="text-sm">{t('suicideQuestions.thoughts.description2')}</p>
              <p className="text-sm">{t('suicideQuestions.thoughts.question')}</p>
            </div>
            <div className="space-y-2">
              {thoughtsOptions.map((option, index) => (
                <Button
                  variant="primary"
                  key={index}
                  onClick={() => {
                    analyticsService.trackEvent({
                      eventName: 'suicidalThoughtsOptionSelected',
                      properties: { from: pages.THOUGHTS, to: option.page, optionId: option.id },
                    });
                    analyticsService.trackEvent({
                      eventName: option.severityEvent,
                    });
                    setCurrentPage(option.page);
                  }}
                >
                  {option.text}
                </Button>
              ))}
              <Button
                variant="secondary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsBackClicked',
                    properties: { from: pages.THOUGHTS, to: pages.INITIAL },
                  });
                  setCurrentPage(pages.INITIAL);
                }}
              >
                {t('common.buttons.back')}
              </Button>
            </div>
          </PrimarySection>
        );

      case pages.INFORMATION:
        return (
          <>
            <PrimarySection title={t('suicideQuestions.title')}>
              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('suicideQuestions.information.description')}
                </p>
                <InformationCards stacks={suicideStacks || []} />
                <h3 className="mx-auto mb-6 max-w-[200px] text-center text-xl font-bold sm:max-w-none">
                  {t('suicideQuestions.information.helpTitle')}
                </h3>
                <HelpCards />
              </div>
            </PrimarySection>

            <div className="space-y-4">
              <div className="w-full">
                <a
                  href={t('suicideQuestions.information.chatButtonLink')}
                  target="_blank"
                  onClick={() =>
                    analyticsService.trackEvent({
                      eventName: 'suicidalThoughtsChatLinkClicked',
                      properties: { from: pages.INFORMATION },
                    })
                  }
                  className="flex h-auto w-full justify-center rounded border border-primary bg-primary p-2 font-button text-primary-foreground"
                >
                  {t('suicideQuestions.information.chatButton')}
                </a>
              </div>
              <Button
                variant="primary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsContinueClicked',
                    properties: { from: pages.INFORMATION, to: pages.CONTINUE },
                  });
                  setCurrentPage(pages.CONTINUE);
                  onClearSuicideRisk();
                }}
              >
                {t('suicideQuestions.information.continueButton')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsBackClicked',
                    properties: { from: pages.INFORMATION, to: pages.THOUGHTS },
                  });
                  setCurrentPage(pages.THOUGHTS);
                }}
              >
                {t('common.buttons.back')}
              </Button>
            </div>
          </>
        );

      case pages.IMMEDIATE:
        return (
          <>
            <div className="space-y-4">
              <div className="pb-2 pt-4">
                <h3 className="mb-4 text-center text-xl font-bold text-card-foreground">
                  {t('infoCardsSection.title')}
                </h3>
                <p className="mx-3 mb-3 text-center text-sm text-muted-foreground">
                  {t('infoCardsSection.additionalInfo')}
                </p>
                <InformationCards stacks={suicideStacks || []} />
              </div>
            </div>

            <div className={'container my-6 rounded border bg-background shadow-md'}>
              <div className="text-center">
                <div
                  className={`mx-auto overflow-hidden border-b border-t bg-card text-left text-card-foreground`}
                >
                  <div className="space-y-4 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.title')}
                    </h4>
                    <p className="text-sm">{t('suicideQuestions.immediate.emergency')}</p>
                  </div>
                  <div className="border-t space-y-4 border border-x-0 border-b-0 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.dangerousThoughts.title')}
                    </h4>
                    <ul className="list-outside list-disc pl-4 text-sm">
                      <li>{t('emergencyInformation.dangerousThoughts.point1')}</li>
                      <li>{t('emergencyInformation.dangerousThoughts.point2')}</li>
                      <li>{t('emergencyInformation.dangerousThoughts.point3')}</li>
                      {t('emergencyInformation.dangerousThoughts.point4') && (
                        <li>{t('emergencyInformation.dangerousThoughts.point4')}</li>
                      )}
                    </ul>
                  </div>
                  <div className="border-t space-y-4 border border-x-0 border-b-0 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.temporaryState.title')}
                    </h4>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point1')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point2')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point3')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point4')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="w-full">
                <a
                  href={t('suicideQuestions.immediate.chatButtonLink')}
                  target="_blank"
                  onClick={() =>
                    analyticsService.trackEvent({
                      eventName: 'suicidalThoughtsChatLinkClicked',
                      properties: { from: pages.IMMEDIATE },
                    })
                  }
                  className="flex h-auto w-full justify-center rounded border border-primary bg-primary p-2 font-button text-primary-foreground"
                >
                  {t('suicideQuestions.immediate.chatButton')}
                </a>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <Button
                variant="primary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsContinueClicked',
                    properties: { from: pages.IMMEDIATE, to: pages.CONTINUE },
                  });
                  setCurrentPage(pages.CONTINUE);
                  onClearSuicideRisk();
                }}
              >
                {t('suicideQuestions.immediate.continueButton')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsBackClicked',
                    properties: { from: pages.IMMEDIATE, to: pages.THOUGHTS },
                  });
                  setCurrentPage(pages.THOUGHTS);
                }}
              >
                {t('common.buttons.back')}
              </Button>
            </div>
          </>
        );

      case pages.EMERGENCY_ONLY:
        return (
          <>
            <p className="mb-4 text-center text-sm">{t('suicideQuestions.immediate.intro')}</p>

            <div className={'container mb-4 rounded border shadow-md'}>
              <div className="text-center">
                <div
                  className={`mx-auto overflow-hidden border-b border-t bg-card text-left text-card-foreground`}
                >
                  <div className="space-y-4 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.title')}
                    </h4>
                    <p className="text-sm">{t('suicideQuestions.immediate.emergency')}</p>
                  </div>
                  <div className="border-t space-y-4 border border-x-0 border-b-0 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.dangerousThoughts.title')}
                    </h4>
                    <ul className="list-outside list-disc pl-4 text-sm">
                      <li>{t('emergencyInformation.dangerousThoughts.point1')}</li>
                      <li>{t('emergencyInformation.dangerousThoughts.point2')}</li>
                      <li>{t('emergencyInformation.dangerousThoughts.point3')}</li>
                      {t('emergencyInformation.dangerousThoughts.point4') && (
                        <li>{t('emergencyInformation.dangerousThoughts.point4')}</li>
                      )}
                    </ul>
                  </div>
                  <div className="border-t space-y-4 border border-x-0 border-b-0 p-6">
                    <h4 className="text-sm font-semibold text-primary">
                      {t('emergencyInformation.temporaryState.title')}
                    </h4>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point1')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point2')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point3')}</p>
                    <p className="text-sm">{t('emergencyInformation.temporaryState.point4')}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="w-full">
                <a
                  href={t('suicideQuestions.immediate.chatButtonLink')}
                  target="_blank"
                  onClick={() =>
                    analyticsService.trackEvent({
                      eventName: 'suicidalThoughtsChatLinkClicked',
                      properties: { from: pages.EMERGENCY_ONLY },
                    })
                  }
                  className="flex h-auto w-full justify-center rounded border border-primary bg-primary p-2 font-button text-primary-foreground"
                >
                  {t('suicideQuestions.immediate.chatButton')}
                </a>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <Button
                variant="primary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsContinueClicked',
                    properties: { from: pages.EMERGENCY_ONLY, to: pages.CONTINUE },
                  });
                  setCurrentPage(pages.CONTINUE);
                  onClearSuicideRisk();
                }}
              >
                {t('suicideQuestions.immediate.continueButton')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  analyticsService.trackEvent({
                    eventName: 'suicidalThoughtsBackClicked',
                    properties: { from: pages.EMERGENCY_ONLY, to: pages.THOUGHTS },
                  });
                  setCurrentPage(pages.THOUGHTS);
                }}
              >
                {t('common.buttons.back')}
              </Button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div ref={componentRef} className="w-full space-y-6 py-4">
      {renderPage()}
    </div>
  );
};

export default SuicideQuestions;
